import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import {
  LastUpdatedDetails,
  getFormattedDate,
  getUpdatedTime,
} from "views/pages/manage/employeeManagement/userProfile/helper";
import Tooltip from "views/pages/manage/common/customTooltip/CustomTooltip";
import CustomChip from "views/pages/manage/common/customChip/CustomChip";
import { refundStatusChipInfo } from "./constants";
import { ReactComponent as EyeIcon } from "assets/img/svg/eye.svg";
import { ReactComponent as EditIcon } from "assets/img/svg/EditGryIcon.svg";
import { CourseTermPopover } from "../../../views/pages/manage/unAssigned/studentFinanceApproval/epmrCancellation/constant";
import ALoader from "views/pages/manage/common/a-loader";
import { getStatusChipInfo } from "./constants";
import { formattedPrice } from "views/pages/manage/unAssigned/collectPayment/utils";
import styles from "../refundStyles.module.scss";
import ServerDataGrid from "views/pages/manage/common/data-grid/ServerDataGrid";
import { CourseDetailsPopupById } from "views/pages/manage/unAssigned/studentCharges/common";
import {Tooltip as MuiTooltip} from "@mui/material";
const ListComponent = ({
  nextButtonHandler,
  dropdowns,
  rows,
  isUpdatePermission,
  isloading,
  refundReasons,
  paginationInfo,
}) => {
  const [courseMap, setCourseMap] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const openInNewTab = useCallback((url) => {
    window.open(window.location.origin + "/#/admin/" + url, "_blank").focus();
  }, []);

  const canShowEditIcon = useCallback((row) => {
    if (!row) return false;

    const { refundStatus, orderType, approvalStatus = "" } = row;

    const status = refundStatus?.toLowerCase();
    const type = orderType?.toLowerCase();
    const approval = approvalStatus?.toLowerCase();

    if (
      type === "completion_refund" &&
      ["initiated", "expired"].includes(status)
    ) {
      return true;
    }

    if (type === "multi_type_refund" && approval.includes("recheck")) {
      return true;
    }

    return false;
  }, []);

  const columns = useMemo(
    () => [
      {
        key: "psid",
        label: "PSID",
        sortable: true,
        width: 130,
        render: (value) =>
          value ? (
            <span
              onClick={() => {
                openInNewTab(`edp/studentDetails/${value}`);
              }}
              style={{
                cursor: "pointer",
              }}
              className="text-primary text-sm"
            >
              {value}
            </span>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "refundNumber",
        label: "Refund Request No.",
        sortable: true,
        width: 160,
        render: (value) =>
          value ? (
            <div>
              <Tooltip showOnClick={true} title={value}>
                <div className={`ellipsis-text-container`}>{value}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "studentPoId",
        label: "Student PO ID",
        sortable: true,
        width: 160,
        render: (value) =>
          value ? (
            <Tooltip showOnClick={true} title={value}>
              <div className="ellipsis-text-container">{value}</div>
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        key: "loanPoId",
        label: "Loan PO ID",
        sortable: true,
        width: 160,
        render: (value) =>
          value ? (
            <Tooltip showOnClick={true} title={value}>
              <div className="ellipsis-text-container">{value}</div>
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        key: "applicationId",
        label: "Application Id",
        sortable: true,
        width: 160,
        render: (value) =>
          value ? (
            <div>
              <Tooltip showOnClick={true} title={value}>
                <div className={`ellipsis-text-container`}>{value}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "studentName",
        label: "Student Name",
        sortable: true,
        width: 130,
        render: (value) =>
          value ? (
            <div>
              <Tooltip showOnClick={true} title={value}>
                <div className={`ellipsis-text-container`}>{value}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "fatherName",
        label: "Parent Name",
        sortable: true,
        width: 110,
        render: (value) =>
          value ? (
            <div>
              <Tooltip title={value}>
                <div className={`ellipsis-text-container`}>{value}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "businessArea",
        label: "BU (Business Unit)",
        sortable: false,
        width: 160,
        render: (value) => (
          <div>
            <span>{value?.label}</span>&nbsp;
            <Tooltip showOnClick={true} title={value?.businessAreaDesc}>
              <span>
                <EyeIcon />
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: "cashierOffice",
        label: "Cashier Office",
        sortable: false,
        width: 160,
        render: (value) => (
          <div>
            <span>{value?.label}</span>&nbsp;
            <Tooltip showOnClick={true} title={value?.businessAreaDesc}>
              <span>
                <EyeIcon />
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: "regularCourseId",
        label: "Regular Course ID",
        sortable: true,
        width: 140,
        render: (value, row) =>
          value ? (
            <div className="ellipsis-text-container">
              <span>{value}</span>
              <span className="ml-2">
                 <CourseDetailsPopupById
                    courseId={value}
                    cta={<EyeIcon />}
                  />
              </span>
            </div>
          ) : (
            "-"
          ),
      },
      {
        key: "sdCourseId",
        label: "Refund Course ID",
        sortable: true,
        width: 170,
        render: (value, row) => (
          <div>
            <Tooltip showOnClick={true} title={value || row?.regularCourseId}>
              <div className={`ellipsis-text-container`}>
                <span>{value || row?.regularCourseId}</span>&nbsp;
                <span className="cursor">
                  <CourseDetailsPopupById
                    courseId={value || row?.regularCourseId}
                    cta={<EyeIcon />}
                  />
                </span>
              </div>
            </Tooltip>
          </div>
        ),
      },
      {
        key: "termValue",
        label: "Term",
        sortable: true,
        width: 130,
        render: (value) =>
          value ? (
            <div>
              <Tooltip showOnClick={true} title={value}>
                <div className={`ellipsis-text-container`}>{value}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "refundAmount",
        label: "Refund Amount",
        sortable: true,
        width: 130,
        render: (value) =>
          value >= 0 ? (
            <Tooltip title={formattedPrice(value)}>
              <div className={styles.amount}>{formattedPrice(value)}</div>
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        key: "orderType",
        label: "Refund Type",
        sortable: true,
        width: 170,
        render: (value) =>
          value ? (
            <div>
              <Tooltip title={value}>
                <div className={`ellipsis-text-container`}>{value}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "subOrderType",
        label: "Sub Refund Type",
        sortable: true,
        width: 170,
        render: (value) => {
          const dispValue = value?.split("_")?.[0];
          return dispValue ? (
            <div>
              <Tooltip title={dispValue}>
                <div className={`ellipsis-text-container`}>{dispValue}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        key: "createdOn",
        label: "Initiated On",
        sortable: true,
        width: 250,
        render: (value) =>
          value ? (
            <div>
              <Tooltip
                title={`${getFormattedDate(value)} at ${getUpdatedTime(value)}`}
              >
                <div className={`ellipsis-text-container`}>{`${getFormattedDate(
                  value
                )} at ${getUpdatedTime(value)}`}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "updatedOn",
        label: "Last Updated On",
        sortable: true,
        width: 280,
        render: (value, row) =>
          value ? (
            <Tooltip title={LastUpdatedDetails(value, row.updatedBy)}>
              <div className={`ellipsis-text-container`}>
                {LastUpdatedDetails(value, row.updatedBy)}
              </div>
            </Tooltip>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "userActionDate",
        label: "Approved On",
        sortable: false,
        width: 280,
        render: (value, row) => {
          const status = row?.refundStatus?.toLowerCase();
          const isShowColumn =
            status == "completed" ||
            status == "approved" ||
            status?.includes("failed");
          return value && isShowColumn ? (
            <Tooltip title={LastUpdatedDetails(value, row.userId)}>
              <div className={`ellipsis-text-container`}>
                {LastUpdatedDetails(value, row.userId)}
              </div>
            </Tooltip>
          ) : (
            <div>-</div>
          );
        },
      },

      {
        key: "processInstanceId",
        label: "Process ID",
        sortable: true,
        width: 170,
        render: (value) =>
          value ? (
            <div>
              <Tooltip title={value}>
                <div className={`ellipsis-text-container`}>{value}</div>
              </Tooltip>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "refundStatus",
        label: "Refund Status",
        sortable: false,
        width: 160,
        render: (value, row) => {
          const status = value?.toLowerCase();
          const chipData = refundStatusChipInfo[status];
          if (!status || !chipData) return <div>-</div>;
          return (
            <CustomChip
              className="text-wrap mt-1 w-100 text-center"
              {...getStatusChipInfo(row, refundReasons)}
            />
          );
        },
      },
      {
        key: "actions",
        align: "right",
        sortable: false,
        label: "Actions",
        width: 120,
        pinned: true,
        render: (_, row) => {
          const showEditIcon = canShowEditIcon(row);
          const {
            psid,
            applicationId,
            refundNumber,
            refundAmount,
            orderType,
            subOrderType,
          } = row;
          const refundType = orderType?.toLowerCase();
          const redirectUrl =
            refundType == "completion_refund"
              ? `reinitiateRefundRequest?psid=${psid}&applicationId=${encodeURIComponent(
                  applicationId
                )}&refundNumber=${encodeURIComponent(
                  refundNumber
                )}&refundAmount=${encodeURIComponent(
                  refundAmount
                )}&orderType=${encodeURIComponent(
                  orderType
                )}&subOrderType=${encodeURIComponent(subOrderType)}`
              : refundType == "multi_type_refund"
              ? `studentRefundRequest/edit/${refundNumber}`
              : ``;
          return (
            <div>
              <MuiTooltip title="View">
                <span
                  onClick={() => {
                    openInNewTab(
                      `viewStudentRefundApproval?psid=${psid}&applicationId=${encodeURIComponent(
                        applicationId
                      )}&refundFlow=${encodeURIComponent(
                        "tracker"
                      )}&refundNumber=${encodeURIComponent(refundNumber)}`
                    );
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <EyeIcon className="ml-2 mt-2 cursor" />
                </span>
              </MuiTooltip>

              {showEditIcon && isUpdatePermission && (
                <MuiTooltip title="Edit" className="ml-3">
                  <span
                    onClick={() => {
                      openInNewTab(redirectUrl);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <EditIcon />
                  </span>
                </MuiTooltip>
              )}
            </div>
          );
        },
      },
    ],
    [
      openInNewTab,
      canShowEditIcon,
      courseMap,
      refundReasons,
      isUpdatePermission,
    ]
  );

  const pagination = {
    page: (paginationInfo?.currPageNo ?? 0) + 1,
    pageSize: paginationInfo?.currPageSize || 10,
    total: paginationInfo?.totalRecords || 0,
    totalPages: paginationInfo?.totalPage || 0,
  };

  return (
    <>
      {isloading && <ALoader position="fixed" />}
      <div className="mt-3">
        <ServerDataGrid
          rows={rows || []}
          loading={false}
          columns={columns}
          rowKey="id"
          pagination={pagination}
          onPageChange={(p) => nextButtonHandler(p - 1, pagination.pageSize)}
          onPageSizeChange={(s) => nextButtonHandler(0, s)}
          sortConfig={sortConfig}
          onSortChange={(key, direction) => setSortConfig({ key, direction })}
        />
      </div>
    </>
  );
};

export default ListComponent;
